/**
 * TikTok Scopes Module (Story 11.1, Task 2)
 *
 * TikTok scope configuration, parsing, and validation.
 *
 * Key differences from LinkedIn/Meta:
 * - Comma-separated scopes (NOT space-separated)
 * - All-or-nothing consent (no partial scope grants)
 * - Single platform: tiktok-business (no multi-platform like Meta/LinkedIn)
 * - user.info.basic is auto-granted for all Login Kit apps
 */

export type TikTokPlatform = "tiktok-business";

export const TIKTOK_PLATFORM_VALUES: TikTokPlatform[] = ["tiktok-business"];

/**
 * Scopes for organic + ads data access via TikTok Login Kit v2 + Marketing API.
 * Order matters for URL consistency.
 *
 * Ads scopes (ad.read, ad.performance.read) require TikTok developer portal approval.
 * If not approved, the OAuth flow will still succeed with organic-only scopes,
 * and the adapter will gracefully fall back to organic-only data.
 */
export const TIKTOK_SCOPES = [
  "user.info.basic", // open_id, display_name, avatar (auto-granted)
  "user.info.stats", // follower_count, following_count, likes_count, video_count
  "video.list", // Read user's public videos
  "ad.read", // Read advertiser account info (Marketing API — requires approval)
  "ad.performance.read", // Read ads reporting data (Marketing API — requires approval)
] as const;

export type TikTokScope = (typeof TIKTOK_SCOPES)[number];

/**
 * Organic (Login Kit v2) subset of {@link TIKTOK_SCOPES} (Story 31.22, AC #4).
 *
 * Stamped onto the organic-user catalog entry's `scopes` field by
 * `discoverAccounts` — display/consent metadata ONLY, never access control
 * (Story 31-4 contract). A drift-guard test in `scopes.test.ts` asserts
 * `TIKTOK_ORGANIC_SCOPES ∪ TIKTOK_ADS_SCOPES === TIKTOK_SCOPES` exactly, so
 * this list cannot silently diverge from the requested-scopes constant.
 */
export const TIKTOK_ORGANIC_SCOPES = [
  "user.info.basic",
  "user.info.stats",
  "video.list",
] as const;

/**
 * Marketing API subset of {@link TIKTOK_SCOPES} (Story 31.22, AC #4).
 *
 * Stamped onto advertiser catalog entries' `scopes` field by
 * `discoverAccounts`. `ad.read` membership in the PERSISTED grant scopes is
 * also the capability gate for advertiser discovery (gate reads
 * `credentials.scopes` only to SKIP work — never to grant access).
 * Same drift-guard test as {@link TIKTOK_ORGANIC_SCOPES}.
 */
export const TIKTOK_ADS_SCOPES = [
  "ad.read",
  "ad.performance.read",
] as const;

/** TikTok uses COMMA as scope separator (not space like LinkedIn, not pipe like others) */
export const TIKTOK_SCOPE_SEPARATOR = ",";

/**
 * Joins scopes into a comma-separated string for TikTok API.
 */
export function buildTikTokScopeString(scopes: readonly string[]): string {
  return scopes.join(TIKTOK_SCOPE_SEPARATOR);
}

/**
 * Parses a comma-separated scope string from TikTok API responses.
 */
export function parseTikTokScopes(scopeString: string): string[] {
  return scopeString
    .split(TIKTOK_SCOPE_SEPARATOR)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Validates that required scopes are present in granted scopes.
 * TikTok is all-or-nothing, but we still verify user.info.basic
 * (auto-granted for all Login Kit apps).
 */
export function validateTikTokScopes(grantedScopes: string[]): {
  valid: boolean;
  missing: string[];
} {
  const required = ["user.info.basic"]; // Auto-granted, but verify
  const missing = required.filter((s) => !grantedScopes.includes(s));
  return { valid: missing.length === 0, missing };
}
